import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Historia.module.css';
import { useScrollAnimation } from '../../../ganchos/usarAnimacionScroll';

/* ── Contador animado con ease-out cúbico ── */
const useCounter = (hasta, duracion = 1800) => {
  const [valor, setValor] = useState(0);
  const rafRef = useRef(null);

  const iniciar = () => {
    let inicio = null;
    const tick = (ts) => {
      if (!inicio) inicio = ts;
      const p = Math.min((ts - inicio) / duracion, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setValor(Math.floor(ease * hasta));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);
  return [valor, iniciar];
};

/* ── Galería ── */
const galeria = [
  { src: '/imagenes/historia/seima-edificio.webp', alt: 'Edificio SEIMA Guatemala' },
  null,
  null,
];

const SeccionHistoria = () => {
  const [seccionRef, esVisible] = useScrollAnimation({ threshold: 0.08 });
  const [indice, setIndice] = useState(0);
  const [contadorActivo, setContadorActivo] = useState(false);
  const [años, iniciarAños] = useCounter(30, 1600);
  const [fundacion, iniciarFundacion] = useCounter(1991, 2200);

  useEffect(() => {
    if (esVisible && !contadorActivo) {
      setContadorActivo(true);
      iniciarAños();
      iniciarFundacion();
    }
  }, [esVisible, contadorActivo, iniciarAños, iniciarFundacion]);

  const anterior = () => setIndice(i => (i - 1 + galeria.length) % galeria.length);
  const siguiente = () => setIndice(i => (i + 1) % galeria.length);
  const item = galeria[indice];

  return (
    <section
      id="historia"
      ref={seccionRef}
      className={`${styles.seccion} ${esVisible ? styles.visible : ''}`}
    >
      <div className={`container ${styles.grid}`}>

        {/* ── Columna izquierda: texto ── */}
        <div className={styles.texto}>

          {/* Título palabra a palabra */}
          <h2 className={styles.titulo}>
            {['Nuestra', 'Historia'].map((palabra, i) => (
              <span
                key={i}
                className={`${styles.palabraWrap} ${esVisible ? styles.palabraVisible : ''}`}
                style={{ transitionDelay: `${i * 140}ms` }}
              >
                {i === 1
                  ? <span className={styles.acento}>{palabra}</span>
                  : palabra}
              </span>
            ))}
          </h2>

          {/* Línea animada bajo el título */}
          <div className={`${styles.lineaTitulo} ${esVisible ? styles.lineaVisible : ''}`} />

          {/* Stats con contadores */}
          <div className={styles.stats}>
            <div className={`${styles.stat} ${esVisible ? styles.statVisible : ''}`} style={{ transitionDelay: '320ms' }}>
              <span className={styles.statNum}>
                {años}<span className={styles.plus}>+</span>
              </span>
              <span className={styles.statLabel}>Años en el mercado</span>
            </div>

            <div className={`${styles.separador} ${esVisible ? styles.sepVisible : ''}`} style={{ transitionDelay: '420ms' }} />

            <div className={`${styles.stat} ${esVisible ? styles.statVisible : ''}`} style={{ transitionDelay: '520ms' }}>
              <span className={`${styles.statNum} ${styles.statAño}`}>
                {fundacion}
              </span>
              <span className={styles.statLabel}>Fundados en Guatemala</span>
            </div>
          </div>

          {/* Párrafos con reveal de izquierda a derecha */}
          <p
            className={`${styles.parrafo} ${esVisible ? styles.parrafoVisible : ''}`}
            style={{ transitionDelay: '600ms' }}
          >
            Durante más de tres décadas hemos sido líderes en la venta, instalación y
            mantenimiento de equipos de lavandería industrial, representando marcas de
            prestigio como <strong>DOMUS</strong> y <strong>MAYTAG</strong>.
          </p>
          <p
            className={`${styles.parrafo} ${esVisible ? styles.parrafoVisible : ''}`}
            style={{ transitionDelay: '720ms' }}
          >
            Nuestro enfoque es directo: equipos que duran más, consumen menos y operan
            sin fallos para garantizar productividad y ahorro real.
          </p>

          <Link
            to="/nosotros"
            className={`btn btn-primary ${styles.cta} ${esVisible ? styles.ctaVisible : ''}`}
            style={{ transitionDelay: '860ms' }}
          >
            Conocer más de nuestra historia
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              strokeWidth={2} stroke="currentColor" width="18" height="18">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>

        {/* ── Columna derecha: galería ── */}
        <div className={`${styles.galeria} ${esVisible ? styles.galeriaVisible : ''}`}>
          <div className={styles.marcoCuadro}>
            {item ? (
              <img
                key={indice}
                src={item.src}
                alt={item.alt}
                className={styles.foto}
              />
            ) : (
              <div className={styles.placeholder}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="1" className={styles.placeholderIcon}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <span>Próximamente</span>
              </div>
            )}

            <div className={styles.overlay} />

            <button className={`${styles.btnFlecha} ${styles.izq}`} onClick={anterior} aria-label="Anterior">‹</button>
            <button className={`${styles.btnFlecha} ${styles.der}`} onClick={siguiente} aria-label="Siguiente">›</button>

            <div className={styles.dots}>
              {galeria.map((_, i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === indice ? styles.dotActivo : ''}`}
                  onClick={() => setIndice(i)}
                  aria-label={`Imagen ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={styles.etiqueta}>
            <span className={styles.etiquetaLinea} />
            <span className={styles.etiquetaTexto}>Desde 1991 · Guatemala</span>
          </div>
        </div>

      </div>
    </section>
  );
};

export default SeccionHistoria;
